import 'date-fns';
import React, { useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as XDatePicker } from '@mui/x-date-pickers';
import isWeekend from 'date-fns/isWeekend';
import { getLocale, translate } from 'translations/translator';
import CommonTranslationVariant from 'translations/variants/common';
import SpanishDateUtils from 'date-fns/locale/es';
import { remCalc } from 'util/style-util';
import { Box, Typography } from '@mui/material';

const getTranslation = (key: CommonTranslationVariant) => translate('common', key);

type DatePickerProps = {
  name: string;
  onChange: (name: string, value: any) => void;
  format?: string;
  sx?: React.CSSProperties;
  value: any;
  label: any;
  defaultValue?: any;
  shouldDisableDate?: any;
  shouldDisableWeekends?: boolean;
};

const DatePicker: React.FC<DatePickerProps> = ({
  onChange,
  name,
  value,
  label,
  defaultValue,
  shouldDisableDate = () => false,
  shouldDisableWeekends,
  format = getTranslation(CommonTranslationVariant.uiDateFormat),
  sx
}) => {
  const handleChange = useCallback(
    (date: Date | null): void => {
      onChange(name, date);
    },
    [onChange, name]
  );

  const MaybeSetSpanishLocale = getLocale() === 'es-US' && {
    locale: SpanishDateUtils
  };

  const disableWeekends = date => isWeekend(date);

  const getDisabledDates = date => {
    if (!shouldDisableDate && !shouldDisableDate) return false;
    if (shouldDisableWeekends) {
      return disableWeekends(date) || shouldDisableDate?.(date);
    }
    return shouldDisableDate?.(date);
  };

  const { width, marginTop, ...restSx } = sx ?? {};

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: width ?? '100%',
        marginTop: marginTop ?? '0'
      }}
    >
      {label && (
        <Typography variant="caption" sx={{ fontSize: '12px', fontWeight: 400 }}>
          {label}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns} {...MaybeSetSpanishLocale}>
        <XDatePicker
          sx={{
            width: '100%',
            fontSize: remCalc(12),
            border: '2px solid',
            borderRadius: '8px',
            fontWeight: 400,
            ...restSx
          }}
          closeOnSelect={false}
          value={new Date(value) ?? Date.now()}
          onChange={handleChange}
          format={format}
          defaultValue={defaultValue ?? ''}
          shouldDisableDate={getDisabledDates}
          slotProps={{
            textField: {
              InputProps: {
                readOnly: true
              }
            }
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DatePicker;
